/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionCopyToClipboard';

const ActionCopyToClipboardManager: ActionManagerFn<'ActionCopyToClipboard'> = (
  platform: Platform,
): ActionManager<'ActionCopyToClipboard'> => {
  return {
    ...baseActionManager(platform, 'ActionCopyToClipboard'),
    defaultValue,
    props: {
      ...baseActionProps,
      value: {
        label: 'Value',
        type: 'expression',
        returnType: async () => 'string',
        required: true,
      },
      message: {
        label: 'Message',
        type: 'string',
        helpText:
          'Specify a message to display an alert after copy to clipboard.',
      },
    },
    name: 'Copy To Clipboard',
    icon: { iconName: 'copy' },
    description: `**Overview**<br/>
The Copy To Clipboard action is a system integration component designed to interact with the user's operating system clipboard. Its primary function is to programmatically copy a specific text string to the clipboard, allowing users to paste that data elsewhere.<br/><br/>
This component combines clipboard functionality with an integrated feedback mechanism. It lets the builder define the text to copy using an expression and optionally configure a success message (alert) shown after the copy operation completes.

**Key Features**<br/>
* **Dynamic Content Source:** Uses an expression property for the value field, enabling the component to copy dynamic data (variables, concatenations, record IDs) rather than only static text.
* **Integrated User Feedback:** Includes an optional message property that displays an alert on success, avoiding the need for a separate "Show Message" action for basic confirmation.

**Properties**<br/>
* **Value (Required):** An expression defining the content to be copied.
  * *Type:* Expression returning a string.
  * *Logic:* The evaluated result is the text payload sent to the clipboard.
* **Message:** A string property for user notification.
  * *Function:* If provided, this text is displayed as an alert after the copy operation finishes.

**Use Cases**<br/>
* **Sharing:** Copying a generated Share URL/deeplink for the user to paste into email or chat.
* **Reference Data:** Letting a user quickly copy a Transaction ID, API Key, or Error Code for support or debugging.`,
  };
};

export default ActionCopyToClipboardManager;
